import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getMainStage,
  inferStageFromTitle,
  UNASSIGNED_STAGE,
  isManualScheduleProjectType,
} from '@/lib/scheduleStageMapping';
import {
  normalizeStoredProjectStage,
  updateProjectCurrentStage,
} from '@/lib/projectStageUpdate';

export {
  getMainStage,
  inferStageFromTitle,
  UNASSIGNED_STAGE,
  isManualScheduleProjectType,
} from '@/lib/scheduleStageMapping';
import { persistProjectSummary } from '@/lib/persistProjectSummary';
import type { ScheduleItem } from '@/lib/scheduleUtils';
import { computeChecklistProgress, isChecklistItemComplete as isCompleteHelper, computeStageChecklistProgress } from '@/lib/checklistProgress';
import type { ChecklistProgress } from '@/lib/checklistProgress';
import { isScheduleItemFinalComplete } from '@/lib/scheduleChecklistProgress';
import { deduplicateChecklistItems } from '@/lib/checklistDedup';
import { JPH_DEBUG, jphDebugLog } from '@/lib/jphDebug';

export interface ChecklistItemMinimal {
  id: string;
  title: string;
  is_completed: boolean;
  management_checked: boolean;
  management_checked_at: string | null;
  management_checked_by: string | null;
  subcontractor_checked: boolean;
  subcontractor_checked_at: string | null;
  subcontractor_checked_by: string | null;
  category: string;
  checklist_id: string;
  checklist_title: string | null;
  description: string | null;
  notes: string | null;
  sort_order: number | null;
  completed_at: string | null;
  completed_by: string | null;
  updated_at: string | null;
  // Requirements
  requires_management_check: boolean;
  requires_subcontractor_check: boolean;
  // Template section info for By Section grouping
  source_section_title: string | null;
  source_section_slug: string | null;
  source_template_item_id: string | null;
  source_template_section_id: string | null;
}

export interface MappingRow {
  id: string;
  checklist_template_item_id: string | null;
  schedule_template_item_id: string | null;
  project_checklist_item_id: string | null;
  project_schedule_item_id: string | null;
  mapping_type: string | null;
  confidence: string | null;
  reason: string | null;
}

export interface LinkedChecklistItem {
  id: string;
  title: string;
  is_completed: boolean;
  management_checked: boolean;
  subcontractor_checked: boolean;
  category: string;
  checklistTitle: string | null;
  confidence: string | null;
  mappingType: string | null;
  mappingId: string | null;
  requires_management_check?: boolean;
  requires_subcontractor_check?: boolean;
}

export interface StageScheduleItem {
  id: string;
  task_no: number | null;
  title: string;
  start_date: string | null;
  end_date: string | null;
  progress: number;
  is_complete: boolean;
  phase: string | null;
  main_stage: string | null;
  construction_sub_stage: string | null;
  delay_status: ScheduleItem['delay_status'];
  linkedChecklistCount: number;
  completedChecklistCount: number;
  // Approval unit metadata for UI display
  managementCheckedCount?: number;
  subcontractorCheckedCount?: number;
  missingManagement?: number;
  missingSubcontractor?: number;
  // Final completion state — unmapped items = true so they don't block 100%
  is_complete_final?: boolean;
  /** Reason for the final progress state — shown in UI for transparency */
  finalProgressReason: 'checklist_complete' | 'checklist_incomplete' | 'not_mapped' | 'manual_complete' | 'manual_incomplete' | 'data_error';
}

export interface ScheduleStageProgress {
  totalItems: number;
  completedItems: number;
  unfinishedItems: number;
  progressPercentage: number;
  /** Sum of all schedule item progress values for averaging */
  totalProgress: number;
}

export interface ConstructionSubStageData {
  name: string;
  paymentPercent: number;
  icon: string;
  accentColor: string;
  bgColor: string;
  scheduleProgress: ScheduleStageProgress;
  checklistCount: number;
  completedChecklistCount: number;
  checklistCompletionPct: number;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Delayed';
  scheduleItems: StageScheduleItem[];
  /** Combined progress = (scheduleProgress * 0.5) + (checklistFinalProgress * 0.5) */
  combinedProgress: number;
  /** Binary schedule progress: completedItems / totalItems * 100 */
  scheduleBinaryPct: number;
  /** Checklist final approval %: finalCompleted / finalTotal * 100 */
  checklistFinalPct: number;
}

export interface StageData {
  name: string;
  icon: string;
  accentColor: string;
  bgColor: string;
  scheduleProgress: ScheduleStageProgress;
  checklistCount: number;
  completedChecklistCount: number;
  checklistCompletionPct: number;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Delayed';
  scheduleItems: StageScheduleItem[];
  constructionSubStages: ConstructionSubStageData[] | null;
  /** Combined progress = (scheduleProgress * 0.5) + (checklistFinalProgress * 0.5) */
  combinedProgress: number;
  /** Binary schedule progress: completedItems / totalItems * 100 */
  scheduleBinaryPct: number;
  /** Checklist final approval %: finalCompleted / finalTotal * 100 */
  checklistFinalPct: number;
  /** Final schedule progress — unmapped items count as complete */
  scheduleFinalProgress: number;
  /** Raw final schedule progress object for external consumers */
  scheduleProgressFinal: ScheduleStageProgress;
}

const STAGE_CONFIG: Record<string, { icon: string; accent: string; bg: string }> = {
  Planning:     { icon: 'ri-map-pin-line',         accent: '#D97706', bg: '#FFFBEB' },
  Design:       { icon: 'ri-pencil-ruler-line',    accent: '#0D9488', bg: '#ECFDF5' },
  Consent:      { icon: 'ri-file-shield-line',     accent: '#E8621A', bg: '#FEF0E7' },
  Construction: { icon: 'ri-building-2-line',      accent: '#059669', bg: '#F0FDF4' },
  Handover:     { icon: 'ri-key-2-line',           accent: '#10B981', bg: '#ECFDF5' },
  Completed:    { icon: 'ri-checkbox-circle-line', accent: '#059669', bg: '#F0FDF4' },
  [UNASSIGNED_STAGE]: { icon: 'ri-question-line', accent: '#6B7280', bg: '#F9FAFB' },
};

export const CONSTRUCTION_SUB_STAGE_ORDER = ['Base', 'Frame', 'Lock Up', 'Fixing', 'Completion', 'Settlement'];

export const CONSTRUCTION_SUB_STAGE_CONFIG: Record<string, {
  name: string;
  paymentPercent: number;
  icon: string;
  accentColor: string;
  bgColor: string;
}> = {
  Base:        { name: 'Base',        paymentPercent: 15, icon: 'ri-building-line',          accentColor: '#D97706', bgColor: '#FEF3C7' },
  Frame:       { name: 'Frame',       paymentPercent: 25, icon: 'ri-layout-4-line',         accentColor: '#E8621A', bgColor: '#FEF0E7' },
  'Lock Up':   { name: 'Lock Up',     paymentPercent: 25, icon: 'ri-lock-unlock-line',      accentColor: '#EF4444', bgColor: '#FEF2F2' },
  Fixing:      { name: 'Fixing',      paymentPercent: 20, icon: 'ri-tools-line',             accentColor: '#059669', bgColor: '#F0FDF4' },
  Completion:  { name: 'Completion',  paymentPercent: 0,  icon: 'ri-checkbox-circle-line',   accentColor: '#10B981', bgColor: '#ECFDF5' },
  Settlement:  { name: 'Settlement',  paymentPercent: 10, icon: 'ri-exchange-line',         accentColor: '#0891B2', bgColor: '#ECFEFF' },
};

export const DEPOSIT_PAYMENT = { name: 'Deposit', paymentPercent: 5, icon: 'ri-wallet-line', accentColor: '#7C3AED', bgColor: '#F5F3FF' };

function isChecklistItemComplete(item: ChecklistItemMinimal): boolean {
  return isCompleteHelper(item);
}

export function computeLinkedProgress(linked: LinkedChecklistItem[]): {
  progressPct: number;
  allRequiredComplete: boolean;
  managementCheckedCount: number;
  subcontractorCheckedCount: number;
  missingManagement: number;
  missingSubcontractor: number;
  totalRequiredUnits: number;
  totalCompletedUnits: number;
} {
  if (linked.length === 0) return {
    progressPct: 0,
    allRequiredComplete: true,
    managementCheckedCount: 0,
    subcontractorCheckedCount: 0,
    missingManagement: 0,
    missingSubcontractor: 0,
    totalRequiredUnits: 0,
    totalCompletedUnits: 0,
  };

  let completed = 0;
  let totalRequiredUnits = 0;
  let totalCompletedUnits = 0;
  let mgmtChecked = 0;
  let subChecked = 0;
  let missingMgmt = 0;
  let missingSub = 0;

  for (const item of linked) {
    const requiresMgmt = item.requires_management_check !== false;
    const requiresSub = item.requires_subcontractor_check === true;
    const mgmtOk = !requiresMgmt || item.management_checked;
    const subOk = !requiresSub || item.subcontractor_checked;
    if (mgmtOk && subOk) completed++;

    if (requiresMgmt) totalRequiredUnits += 1;
    if (requiresSub) totalRequiredUnits += 1;
    if (requiresMgmt && item.management_checked) { totalCompletedUnits += 1; mgmtChecked++; }
    else if (requiresMgmt) missingMgmt++;
    if (requiresSub && item.subcontractor_checked) { totalCompletedUnits += 1; subChecked++; }
    else if (requiresSub) missingSub++;
  }

  const progressPct = totalRequiredUnits > 0
    ? Math.round((totalCompletedUnits / totalRequiredUnits) * 100)
    : 0;

  return {
    progressPct,
    allRequiredComplete: completed >= linked.length,
    managementCheckedCount: mgmtChecked,
    subcontractorCheckedCount: subChecked,
    missingManagement: missingMgmt,
    missingSubcontractor: missingSub,
    totalRequiredUnits,
    totalCompletedUnits,
  };
}

/**
 * Compute stage status using combined schedule + checklist logic.
 *
 * Rules:
 * - Not Started: schedule progress = 0 AND checklist final progress = 0
 * - Completed: schedule final = 100% AND (checklist final = 100% OR stage has 0 checklist items)
 * - Delayed: any item is delayed (and not fully completed)
 * - In Progress: anything in between
 *
 * A stage with NO checklist items should not be blocked by checklist progress.
 * It should only require schedule completion (e.g. Consent stage with unmapped items).
 * A stage WITH checklist items requires BOTH schedule 100% AND checklist 100% to be Completed.
 */
function computeStageStatus(
  stageProgress: ScheduleStageProgress,
  stageProgressFinal: ScheduleStageProgress,
  scheduleItems: StageScheduleItem[],
  checklistFinalPct: number,
  checklistCount: number
): StageData['status'] {
  if (stageProgress.totalItems === 0) return 'Not Started';

  // Use FINAL progress for completion check (unmapped = complete)
  const scheduleBinaryPctFinal = stageProgressFinal.totalItems > 0
    ? Math.round((stageProgressFinal.completedItems / stageProgressFinal.totalItems) * 100)
    : 0;

  // Completed: BOTH final schedule and checklist must be 100%
  // EXCEPT: if stage has 0 checklist items, checklist doesn't block completion
  const checklistBlocks = checklistCount > 0;
  if (scheduleBinaryPctFinal === 100 && (!checklistBlocks || checklistFinalPct === 100)) {
    return 'Completed';
  }

  // Not Started: both are 0
  if (scheduleBinaryPctFinal === 0 && checklistFinalPct === 0) {
    const anyInProgress = scheduleItems.some((i) => i.progress > 0 || i.is_complete);
    if (!anyInProgress) return 'Not Started';
  }

  // Delayed: any item is delayed (but not fully completed)
  const anyDelayed = scheduleItems.some(
    (i) => i.delay_status === 'delayed' || i.delay_status === 'completed_late'
  );
  if (anyDelayed) return 'Delayed';

  return 'In Progress';
}

/**
 * Infer Construction sub-stage from title.
 * Detailed classification per user's specification.
 */
export function inferConstructionSubStage(title: string): string | null {
  const lower = title.toLowerCase();

  // Settlement — payment milestone closeout items
  if (/(\bsettlement\b|\binsurance change\b|\bfinal payment\b|\bowner final inspection\b|\bccc application\b)/.test(lower)) return 'Settlement';

  // Completion — final inspection and cleanup
  if (/(\bfinal internal inspection\b|\bfinal touch ups\b|\bfinal photos\b|\bwarranty setup\b|\bfinal clean\b|\bdefect check\b|\bcleaning\b|\bowner inspection\b|\bjph inspection\b|\bcode compliance\b|\bpractical completion\b)/.test(lower)) return 'Completion';

  // Fixing — interior finishes and fit-out
  if (/(\bpreline\b|\bpostline\b|\bgib\b|\bplaster\b|\binternal linings\b|\bpainting\b|\bpainter\b|\btile\b|\bbathroom\b|\bkitchen cabinet\b|\bkitchen bench\b|\bflooring\b|\belectrical fitoff\b|\bplumbing fitoff\b|\binterior doors\b|\bwardrobes\b|\bstair\b|\bdeck\b|\bdriveway\b|\blandscaping\b|\bfitout\b|\bfit-out\b|\bfit out\b|\bcabinet\b|\bbench\b|\bsplash back\b|\bsplashback\b|\bbathware\b|\bappliance\b|\bvanity\b|\bmirror\b|\bdoor handle\b|\bstopper\b|\bpaper holder\b|\bskirting\b|\bboxing\b|\bceiling\b|\bpower on\b|\bfloor noise\b|\belec final\b|\bplum final\b|\bfinal bed\b|\bfinal 2\b|\btimber floor\b|\bfloor carpet\b)/.test(lower)) return 'Fixing';

  // Lock Up — exterior enclosure
  if (/(\broof\b|\bwrap\b|\bwindow\b|\bexterior door\b|\bsoffit\b|\bexterior cladding\b|\bbrick\b|\bexterior envelope\b|\bweather tight\b|\bgarage door\b|\bdown pipe\b|\bdownpipe\b|\bscaffold\b|\bscaffold down\b|\bexterior paint\b|\belec\b|\bplum\b|\bhirb\b|\binsulation\b|\bglass\b|\bdown pipe\b)/.test(lower)) return 'Lock Up';

  // Frame — structural framing
  if (/(\bwall frame\b|\bfloor joist\b|\bsteel frame\b|\bsteel beam\b|\btruss\b|\bupper floor\b|\blower truss\b|\bframe inspections\b|\bupper truss\b|\bfascia\b|\bgutter\b|\bsafety net\b|\bbeam\b|\bjoist\b|\bjoist\b|\bset out\b)/.test(lower)) return 'Frame';

  // Base — ground works and slab
  if (/(\bsite cut\b|\bexcavation\b|\bsilt fence\b|\bsurveyor set out\b|\bretaining wall\b|\bfooting\b|\bfoundation\b|\bslab\b|\bunder slab\b|\bunder-slab\b|\baquacon\b|\bdrainage before slab\b|\bblock wall\b|\bwaterproof\b|\bback fill\b|\bsite prep\b|\bbase\b|\bground floor\b|\bgf wall\b|\bcivil drain\b|\bdrain work\b|\bservice\b|\btidy up\b|\bsite excavate\b|\btank\b|\bpublic drain\b|\bdemolition\b)/.test(lower)) return 'Base';

  return null;
}

export function getConstructionSubStage(item: ScheduleItem): string | null {
  if (item.construction_sub_stage) return item.construction_sub_stage;
  const mainStage = item.main_stage || item.phase;
  if (mainStage !== 'Construction') return null;
  return inferConstructionSubStage(item.title);
}

const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover', 'Completed'];

// Cross-instance sync: all useStageScheduleChecklist instances on the same project share state
const SYNC_KEY = '__jphph_checklist_sync__';

type SyncEvent = {
  projectId: string;
  itemId: string;
  field: string;
  newValue: boolean;
  isCompleted: boolean;
};

function broadcastSync(e: SyncEvent) {
  try {
    window.dispatchEvent(new CustomEvent(SYNC_KEY, { detail: e }));
  } catch { /* noop */ }
}

export interface UseStageScheduleChecklistOptions {
  /** When false, skip fetch/realtime until enabled (e.g. inactive tab). Default true. */
  enabled?: boolean;
}

export function useStageScheduleChecklist(
  projectId: string,
  options: UseStageScheduleChecklistOptions = {}
) {
  const enabled = options.enabled !== false;
  // CRITICAL FIX: Removed module-level cache (dataCache) entirely.
  // The cache was causing empty-state bugs when it contained stale empty data.
  // Every mount now fetches fresh data directly from Supabase.
  const hasLoadedOnceRef = useRef(false);
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [checklistItems, setChecklistItems] = useState<ChecklistItemMinimal[]>([]);
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [projectStage, setProjectStage] = useState<string | null>(null);
  const [projectType, setProjectType] = useState<string | null>(null);
  const [movingUnassigned, setMovingUnassigned] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState(false);
  const [defaultTemplateItemId, setDefaultTemplateItemId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(async (forceRefresh = false) => {
    if (!projectId || !enabled) return;
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    setError(null);
    try {
      // CRITICAL: jphph_checklist_items.project_id and jphph_checklists.project_id
      // are TEXT columns, not UUID. Always use String(projectId) for these queries.
      // jphph_project_schedule_items.project_id is UUID — use projectId directly.
      const projectIdText = String(projectId);

      // --- EXACT DEBUG LOGS REQUESTED BY USER ---
      jphDebugLog('Checklist current project id', projectId);
      jphDebugLog('Checklist project table source', 'jphph_projects');

      const [
        { data: si, error: siErr },
        { data: ci, error: ciErr },
        { data: checklists, error: clErr },
        { data: templates },
        { data: proj },
      ] = await Promise.all([
        supabase
          .from('jphph_project_schedule_items')
          .select('*')
          .eq('project_id', projectId) // UUID column — use projectId directly
          .order('sort_order', { ascending: true }),
        supabase
          .from('jphph_checklist_items')
          .select('id, title, is_completed, management_checked, management_checked_at, management_checked_by, subcontractor_checked, subcontractor_checked_at, subcontractor_checked_by, category, checklist_id, description, notes, sort_order, completed_at, completed_by, updated_at, requires_management_check, requires_subcontractor_check, source_section_title, source_section_slug, source_template_item_id, source_template_section_id')
          .eq('project_id', projectIdText) // TEXT column — use String(projectId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('jphph_checklists')
          .select('id, title')
          .eq('project_id', projectIdText), // TEXT column — use String(projectId)
        supabase
          .from('jphph_checklist_template_items')
          .select('id')
          .limit(1),
        supabase
          .from('jphph_projects')
          .select('current_stage, status, project_type')
          .eq('id', projectId)
          .maybeSingle(),
      ]);

      // Surface query errors explicitly — do NOT silently swallow them
      let combinedError: string | null = null;
      if (siErr) {
        console.error('[useStageScheduleChecklist] Schedule items query FAILED:', {
          code: siErr.code, message: siErr.message, details: siErr.details,
        });
        combinedError = `Schedule query failed: ${siErr.message} (${siErr.code})`;
      }
      if (ciErr) {
        console.error('[useStageScheduleChecklist] Checklist items query FAILED:', {
          code: ciErr.code, message: ciErr.message, details: ciErr.details,
        });
        combinedError = combinedError
          ? `${combinedError}; Checklist query failed: ${ciErr.message} (${ciErr.code})`
          : `Checklist query failed: ${ciErr.message} (${ciErr.code})`;
      }
      if (clErr) {
        console.error('[useStageScheduleChecklist] Checklists query FAILED:', {
          code: clErr.code, message: clErr.message, details: clErr.details,
        });
      }

      if (combinedError) {
        setError(combinedError);
      }

      const items = (si || []) as ScheduleItem[];
      let checkItems = (ci || []) as Array<Omit<ChecklistItemMinimal, 'checklist_title'>>;

      // CRITICAL FIX: Deduplicate checklist items using shared utility.
      // Handles both template-ID duplicates AND NULL-template-ID duplicates.
      const { unique: dedupedCheckItems, duplicates: duplicateItems } = deduplicateChecklistItems(checkItems);
      checkItems = dedupedCheckItems;

      const clMap = new Map((checklists || []).map((c: { id: string; title: string }) => [c.id, c.title]));

      jphDebugLog('Checklist parent query result', checklists);
      jphDebugLog('Checklist items query result', checkItems);
      jphDebugLog('Checklist load error', combinedError || ciErr?.message || clErr?.message || null);

      jphDebugLog('[useStageScheduleChecklist] Checklist query debug', {
        queriedProjectId: projectId,
        queriedProjectIdText: projectIdText,
        queriedProjectIdType: typeof projectId,
        rawChecklistItemsFound: (ci || []).length,
        dedupedChecklistItemsFound: checkItems.length,
        duplicateChecklistItems: duplicateItems.length,
        duplicateTemplateIds: duplicateItems.map((d) => d.source_template_item_id),
        checklistsFound: (checklists || []).length,
        checklistError: ciErr ? { code: ciErr.code, message: ciErr.message } : null,
        checklistsError: clErr ? { code: clErr.code, message: clErr.message } : null,
      });

      const fullCheckItems: ChecklistItemMinimal[] = checkItems.map((c) => ({
        ...c,
        checklist_title: clMap.get(c.checklist_id) || null,
      }));

      if (templates && templates.length > 0) {
        setDefaultTemplateItemId(templates[0].id);
      }

      setProjectStage(
        normalizeStoredProjectStage((proj as { current_stage?: string | null } | null)?.current_stage)
          ?? normalizeStoredProjectStage((proj as { status?: string | null } | null)?.status)
      );
      setProjectType((proj as { project_type?: string | null } | null)?.project_type ?? null);

      const scheduleIds = items.map((i) => i.id);
      const templateIds = items.map((i) => i.source_template_item_id).filter(Boolean) as string[];
      const checklistIds = fullCheckItems.map((c) => c.id);

      /* ── Fetch mappings in batched chunks to avoid URL length limits ── */
      async function fetchMappingsBatch(column: string, ids: string[], batchSize = 50): Promise<MappingRow[]> {
        const all: MappingRow[] = [];
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize);
          const { data, error } = await supabase
            .from('jphph_checklist_schedule_item_mappings')
            .select('id, checklist_template_item_id, schedule_template_item_id, project_checklist_item_id, project_schedule_item_id, mapping_type, confidence, reason')
            .in(column, batch);
          if (error) {
            // eslint-disable-next-line no-console
            console.error(`Mapping batch query failed (${column} batch ${i}):`, error.message);
            continue;
          }
          all.push(...(data as MappingRow[]));
        }
        return all;
      }

      let allMappings: MappingRow[] = [];
      if (scheduleIds.length > 0) {
        const pm = await fetchMappingsBatch('project_schedule_item_id', scheduleIds);
        allMappings.push(...pm);
        if (import.meta.env.DEV && JPH_DEBUG) {
          jphDebugLog(`[useStageScheduleChecklist] Fetched ${pm.length} mappings by project_schedule_item_id (${scheduleIds.length} schedule IDs)`);
        }
      }
      if (templateIds.length > 0) {
        const tm = await fetchMappingsBatch('schedule_template_item_id', templateIds);
        for (const row of tm) {
          if (!allMappings.some((m) => m.id === row.id)) allMappings.push(row);
        }
        if (import.meta.env.DEV && JPH_DEBUG) {
          jphDebugLog(`[useStageScheduleChecklist] Added ${tm.length} mappings by schedule_template_item_id (${templateIds.length} template IDs)`);
        }
      }
      if (checklistIds.length > 0) {
        const cm = await fetchMappingsBatch('project_checklist_item_id', checklistIds);
        for (const row of cm) {
          if (!allMappings.some((m) => m.id === row.id)) allMappings.push(row);
        }
        if (import.meta.env.DEV && JPH_DEBUG) {
          jphDebugLog(`[useStageScheduleChecklist] Added ${cm.length} mappings by project_checklist_item_id (${checklistIds.length} checklist IDs)`);
        }
      }

      if (import.meta.env.DEV && JPH_DEBUG) {
        jphDebugLog(`[useStageScheduleChecklist] Total mappings loaded: ${allMappings.length}`);
        jphDebugLog(`[useStageScheduleChecklist] Checklist items: ${fullCheckItems.length}, Schedule items: ${items.length}`);
        const mappedChecklistIds = new Set(allMappings.map((m) => m.project_checklist_item_id).filter(Boolean));
        const unmappedCount = fullCheckItems.filter((c) => !mappedChecklistIds.has(c.id)).length;
        jphDebugLog(`[useStageScheduleChecklist] Unmapped checklist items: ${unmappedCount}`);
      }

      setScheduleItems(items);
      setChecklistItems(fullCheckItems);
      setMappings(allMappings);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load stage data');
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [projectId, enabled]);

  useEffect(() => {
    if (enabled) load();
  }, [load, enabled]);

  useEffect(() => {
    if (!projectId || !enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string; newStage: string }>).detail;
      if (detail?.projectId !== projectId || !detail.newStage) return;
      setProjectStage(detail.newStage);
    };
    window.addEventListener('__jphph_stage_changed__', handler);
    return () => window.removeEventListener('__jphph_stage_changed__', handler);
  }, [projectId, enabled]);

  // After initial data load, run a one-time batch sync to fix stale schedule progress values.
  // Skip when data came from cache (sync already ran on original fetch).
  const [hasSynced, setHasSynced] = useState(false);

  // Listen to cross-instance sync events for the same project
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SyncEvent>).detail;
      if (detail.projectId !== projectId) return;
      setChecklistItems((prev) => {
        const idx = prev.findIndex((c) => c.id === detail.itemId);
        if (idx === -1) return prev;
        const old = prev[idx];
        const next = [...prev];
        const field = detail.field as keyof ChecklistItemMinimal;
        const dateField = detail.field === 'subcontractor_checked' ? 'subcontractor_checked_at' : 'management_checked_at';
        const byField = detail.field === 'subcontractor_checked' ? 'subcontractor_checked_by' : 'management_checked_by';
        next[idx] = {
          ...old,
          [field]: detail.newValue,
          [dateField]: detail.newValue ? new Date().toISOString() : null,
          [byField]: detail.newValue ? 'synced' : null,
          is_completed: detail.isCompleted,
          completed_at: detail.isCompleted ? new Date().toISOString() : null,
          completed_by: detail.isCompleted ? 'synced' : null,
        };
        return next;
      });
    };
    window.addEventListener(SYNC_KEY, handler);
    return () => window.removeEventListener(SYNC_KEY, handler);
  }, [projectId, enabled]);

  // Listen to storage events for full reload from other tabs
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: StorageEvent) => {
      if (e.key === SYNC_KEY && e.newValue) {
        const detail: SyncEvent = JSON.parse(e.newValue);
        if (detail.projectId !== projectId) return;
        setChecklistItems((prev) => {
          const idx = prev.findIndex((c) => c.id === detail.itemId);
          if (idx === -1) return prev;
          const old = prev[idx];
          const next = [...prev];
          const field = detail.field as keyof ChecklistItemMinimal;
          const dateField = detail.field === 'subcontractor_checked' ? 'subcontractor_checked_at' : 'management_checked_at';
          const byField = detail.field === 'subcontractor_checked' ? 'subcontractor_checked_by' : 'management_checked_by';
          next[idx] = {
            ...old,
            [field]: detail.newValue,
            [dateField]: detail.newValue ? new Date().toISOString() : null,
            [byField]: detail.newValue ? 'synced' : null,
            is_completed: detail.isCompleted,
            completed_at: detail.isCompleted ? new Date().toISOString() : null,
            completed_by: detail.isCompleted ? 'synced' : null,
          };
          return next;
        });
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [projectId, enabled]);

  // ── CRITICAL FIX: Supabase realtime listener for schedule items ──
  useEffect(() => {
    if (!projectId || !enabled) return;
    const channel = supabase
      .channel(`stage-schedule-items-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_project_schedule_items',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' && payload.new) {
            const newItem = payload.new as ScheduleItem;
            setScheduleItems((prev) => {
              const idx = prev.findIndex((i) => i.id === newItem.id);
              if (idx === -1) return [...prev, newItem];
              const next = [...prev];
              next[idx] = { ...next[idx], ...newItem };
              return next;
            });
          } else if (payload.eventType === 'DELETE' && payload.old) {
            const oldId = payload.old.id as string;
            setScheduleItems((prev) => prev.filter((i) => i.id !== oldId));
          } else if (payload.eventType === 'INSERT' && payload.new) {
            const newItem = payload.new as ScheduleItem;
            setScheduleItems((prev) => [...prev, newItem]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, enabled]);

  // ── CRITICAL FIX: Listen for cross-instance schedule sync broadcasts ──
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string; scheduleItemId?: string; bulkItemIds?: string[]; isComplete: boolean; timestamp: string }>).detail;
      if (detail.projectId !== projectId) return;
      jphDebugLog('Cross-instance schedule sync received', {
        projectId: detail.projectId,
        scheduleItemId: detail.scheduleItemId,
        bulkCount: detail.bulkItemIds?.length ?? 0,
        isComplete: detail.isComplete,
      });
      // Force immediate reload — invalidate cache and refetch
      setHasSynced(false); // allow batch sync to run again
      load(true); // forceRefresh
    };
    window.addEventListener('__jphph_schedule_sync__', handler);
    return () => window.removeEventListener('__jphph_schedule_sync__', handler);
  }, [projectId, load, enabled]);

  // ── Supabase realtime listener for checklist items ──
  useEffect(() => {
    if (!projectId || !enabled) return;
    const channel = supabase
      .channel(`stage-checklist-items-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_checklist_items',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' && payload.new) {
            const newItem = payload.new as ChecklistItemMinimal;
            setChecklistItems((prev) => {
              const idx = prev.findIndex((c) => c.id === newItem.id);
              if (idx === -1) return [...prev, newItem];
              const next = [...prev];
              next[idx] = { ...next[idx], ...newItem };
              return next;
            });
          } else if (payload.eventType === 'DELETE' && payload.old) {
            const oldId = payload.old.id as string;
            setChecklistItems((prev) => prev.filter((c) => c.id !== oldId));
          } else if (payload.eventType === 'INSERT' && payload.new) {
            const newItem = payload.new as ChecklistItemMinimal;
            setChecklistItems((prev) => [...prev, newItem]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, enabled]);

  const linkedMap = useMemo(() => {
    const map = new Map<string, LinkedChecklistItem[]>();
    for (const item of scheduleItems) {
      const linked: LinkedChecklistItem[] = [];
      const seen = new Set<string>();

      const projMappings = mappings.filter((m) => m.project_schedule_item_id === item.id);
      for (const m of projMappings) {
        if (!m.project_checklist_item_id) continue;
        const ci = checklistItems.find((c) => c.id === m.project_checklist_item_id);
        if (ci && !seen.has(ci.id)) {
          seen.add(ci.id);
          linked.push({
            id: ci.id,
            title: ci.title,
            is_completed: isChecklistItemComplete(ci),
            management_checked: ci.management_checked,
            subcontractor_checked: ci.subcontractor_checked,
            category: ci.category,
            checklistTitle: ci.checklist_title,
            confidence: m.confidence,
            mappingType: m.mapping_type,
            mappingId: m.id,
            requires_management_check: ci.requires_management_check,
            requires_subcontractor_check: ci.requires_subcontractor_check,
          });
        }
      }

      if (item.source_template_item_id) {
        const tmplMappings = mappings.filter(
          (m) =>
            m.schedule_template_item_id === item.source_template_item_id &&
            m.project_checklist_item_id &&
            !seen.has(m.project_checklist_item_id)
        );
        for (const m of tmplMappings) {
          const ci = checklistItems.find((c) => c.id === m.project_checklist_item_id);
          if (ci) {
            seen.add(ci.id);
            linked.push({
              id: ci.id,
              title: ci.title,
              is_completed: isChecklistItemComplete(ci),
              management_checked: ci.management_checked,
              subcontractor_checked: ci.subcontractor_checked,
              category: ci.category,
              checklistTitle: ci.checklist_title,
              confidence: m.confidence,
              mappingType: m.mapping_type,
              mappingId: m.id,
              requires_management_check: ci.requires_management_check,
              requires_subcontractor_check: ci.requires_subcontractor_check,
            });
          }
        }
      }

      map.set(item.id, linked);
    }
    return map;
  }, [scheduleItems, checklistItems, mappings]);

  const checklistToSchedulesMap = useMemo(() => {
    const map = new Map<string, { scheduleItemId: string; mappingId: string }[]>();
    for (const mapping of mappings) {
      if (mapping.project_checklist_item_id && mapping.project_schedule_item_id) {
        const existing = map.get(mapping.project_checklist_item_id) || [];
        existing.push({
          scheduleItemId: mapping.project_schedule_item_id,
          mappingId: mapping.id,
        });
        map.set(mapping.project_checklist_item_id, existing);
      }
    }
    return map;
  }, [mappings]);

  const unmappedChecklistItems = useMemo(() => {
    const mappedIds = new Set<string>();
    for (const mapping of mappings) {
      if (mapping.project_checklist_item_id) mappedIds.add(mapping.project_checklist_item_id);
    }
    return checklistItems.filter((ci) => !mappedIds.has(ci.id));
  }, [checklistItems, mappings]);

  // Build stage data with calculated schedule progress and sub-stages for Construction
  const stages = useMemo((): StageData[] => {
    // Build a map of checklist item id -> stage name for per-stage checklist progress
    // We need to know which checklist items belong to which stage via their linked schedule items
    const checklistItemToStage = new Map<string, string>();
    for (const mapping of mappings) {
      if (!mapping.project_checklist_item_id || !mapping.project_schedule_item_id) continue;
      const si = scheduleItems.find((s) => s.id === mapping.project_schedule_item_id);
      if (si) {
        const stageName = getMainStage(si);
        checklistItemToStage.set(mapping.project_checklist_item_id, stageName);
      }
    }

    const stageMap = new Map<string, {
      name: string;
      scheduleItems: StageScheduleItem[];
      scheduleProgress: ScheduleStageProgress;
      checklistCount: number;
      completedChecklistCount: number;
      plannedStartDate: string | null;
      plannedEndDate: string | null;
      // Per-stage checklist items for accurate final approval %
      stageChecklistItems: typeof checklistItems;
      subStageMap: Map<string, {
        scheduleItems: StageScheduleItem[];
        scheduleProgress: ScheduleStageProgress;
        checklistCount: number;
        completedChecklistCount: number;
        plannedStartDate: string | null;
        plannedEndDate: string | null;
        stageChecklistItems: typeof checklistItems;
        scheduleProgressFinal: ScheduleStageProgress;
      }>;
      // Final schedule progress (unmapped = complete)
      scheduleProgressFinal: ScheduleStageProgress;
    }>();

    for (const name of STAGE_ORDER) {
      const subStageMap = new Map<string, {
        scheduleItems: StageScheduleItem[];
        scheduleProgress: ScheduleStageProgress;
        checklistCount: number;
        completedChecklistCount: number;
        plannedStartDate: string | null;
        plannedEndDate: string | null;
        stageChecklistItems: typeof checklistItems;
      }>();
      // Only init sub-stages for Construction
      if (name === 'Construction') {
        for (const sub of CONSTRUCTION_SUB_STAGE_ORDER) {
          subStageMap.set(sub, {
            scheduleItems: [],
            scheduleProgress: { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 },
            checklistCount: 0,
            completedChecklistCount: 0,
            plannedStartDate: null,
            plannedEndDate: null,
            stageChecklistItems: [],
            // Final progress for sub-stage
            scheduleProgressFinal: { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 },
          });
        }
      }
      stageMap.set(name, {
        name,
        scheduleItems: [],
        scheduleProgress: { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 },
        checklistCount: 0,
        completedChecklistCount: 0,
        plannedStartDate: null,
        plannedEndDate: null,
        stageChecklistItems: [],
        subStageMap,
        scheduleProgressFinal: { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 },
      });
    }

    stageMap.set(UNASSIGNED_STAGE, {
      name: UNASSIGNED_STAGE,
      scheduleItems: [],
      scheduleProgress: { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 },
      checklistCount: 0,
      completedChecklistCount: 0,
      plannedStartDate: null,
      plannedEndDate: null,
      stageChecklistItems: [],
      subStageMap: new Map(),
      scheduleProgressFinal: { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 },
    });

    for (const item of scheduleItems) {
      if (item.is_included === false) continue;

      const stageName = getMainStage(item);
      const stage = stageMap.get(stageName);
      if (!stage) continue;

      const linked = linkedMap.get(item.id) || [];
      const linkedComplete = linked.filter((l) => l.is_completed).length;

      // LIVE CALCULATED checklist-derived progress (NOT stale DB item.progress)
      const calc = computeLinkedProgress(linked);
      // For DISPLAY: only use checklist-derived values when linked items exist.
      // If no checklist is linked, fall back to DB fields (manual progress).
      const liveProgress = linked.length > 0 ? calc.progressPct : item.progress;

      // CRITICAL FIX: Single source of truth for completion.
      // Use the shared isScheduleItemFinalComplete function.
      // is_complete DB flag (manual completion) and progress>=100 always count.
      // Also check actual_end_date as a completion signal.
      const liveIsComplete = isScheduleItemFinalComplete(
        { is_complete: item.is_complete, progress: item.progress, actual_end_date: item.actual_end_date },
        linked
      );

      // For FINAL progress: same unified logic
      const liveIsCompleteFinal = liveIsComplete;

      // Determine the reason for the final progress state
      let finalProgressReason: StageScheduleItem['finalProgressReason'];
      if (item.is_complete) {
        finalProgressReason = 'manual_complete';
      } else if (item.progress >= 100) {
        finalProgressReason = 'manual_complete';
      } else if (item.actual_end_date) {
        finalProgressReason = 'manual_complete';
      } else if (linked.length > 0) {
        finalProgressReason = calc.allRequiredComplete ? 'checklist_complete' : 'checklist_incomplete';
      } else if (item.progress > 0) {
        finalProgressReason = 'manual_incomplete';
      } else {
        finalProgressReason = 'not_mapped';
      }

      const ssi: StageScheduleItem = {
        id: item.id,
        task_no: item.task_no,
        title: item.title,
        start_date: item.start_date,
        end_date: item.end_date,
        // DISPLAY: use live calculated progress when linked, DB when not
        progress: liveProgress,
        is_complete: liveIsComplete,
        phase: item.phase,
        main_stage: getMainStage(item),
        construction_sub_stage: getConstructionSubStage(item),
        delay_status: item.delay_status,
        linkedChecklistCount: linked.length,
        completedChecklistCount: linkedComplete,
        // Add approval unit metadata for UI display
        managementCheckedCount: calc.managementCheckedCount,
        subcontractorCheckedCount: calc.subcontractorCheckedCount,
        missingManagement: calc.missingManagement,
        missingSubcontractor: calc.missingSubcontractor,
        // FINAL completion state (for project-level calculations)
        is_complete_final: liveIsCompleteFinal,
        finalProgressReason,
      };

      stage.scheduleItems.push(ssi);
      stage.scheduleProgress.totalItems += 1;
      // Accumulate the live progress value (0-100) for averaging (display only)
      stage.scheduleProgress.totalProgress += liveProgress;
      // DISPLAY binary completion — uses unified completion check
      if (liveIsComplete) {
        stage.scheduleProgress.completedItems += 1;
      } else {
        stage.scheduleProgress.unfinishedItems += 1;
      }
      // FINAL binary completion — uses SAME unified completion check
      if (liveIsCompleteFinal) {
        stage.scheduleProgressFinal.completedItems += 1;
      } else {
        stage.scheduleProgressFinal.unfinishedItems += 1;
      }
      stage.checklistCount += linked.length;
      stage.completedChecklistCount += linkedComplete;

      // Track per-stage checklist items for accurate final approval %
      for (const li of linked) {
        const ci = checklistItems.find((c) => c.id === li.id);
        if (ci && !stage.stageChecklistItems.some((x) => x.id === ci.id)) {
          stage.stageChecklistItems.push(ci);
        }
      }

      if (item.start_date) {
        if (!stage.plannedStartDate || item.start_date < stage.plannedStartDate) stage.plannedStartDate = item.start_date;
      }
      if (item.end_date) {
        if (!stage.plannedEndDate || item.end_date > stage.plannedEndDate) stage.plannedEndDate = item.end_date;
      }

      // Add to sub-stage if Construction
      if (stageName === 'Construction') {
        const subStageName = ssi.construction_sub_stage;
        if (subStageName && stage.subStageMap.has(subStageName)) {
          const sub = stage.subStageMap.get(subStageName)!;
          sub.scheduleItems.push(ssi);
          sub.scheduleProgress.totalItems += 1;
          sub.scheduleProgress.totalProgress += liveProgress;
          // DISPLAY binary — unified check
          if (liveIsComplete) {
            sub.scheduleProgress.completedItems += 1;
          } else {
            sub.scheduleProgress.unfinishedItems += 1;
          }
          // FINAL binary — unified check
          if (liveIsCompleteFinal) {
            sub.scheduleProgressFinal.completedItems += 1;
          } else {
            sub.scheduleProgressFinal.unfinishedItems += 1;
          }
          sub.checklistCount += linked.length;
          sub.completedChecklistCount += linkedComplete;
          // Track per-sub-stage checklist items
          for (const li of linked) {
            const ci = checklistItems.find((c) => c.id === li.id);
            if (ci && !sub.stageChecklistItems.some((x) => x.id === ci.id)) {
              sub.stageChecklistItems.push(ci);
            }
          }
          if (item.start_date) {
            if (!sub.plannedStartDate || item.start_date < sub.plannedStartDate) sub.plannedStartDate = item.start_date;
          }
          if (item.end_date) {
            if (!sub.plannedEndDate || item.end_date > sub.plannedEndDate) sub.plannedEndDate = item.end_date;
          }
        }
      }
    }

    // Calculate percentages — use AVERAGE of schedule item progress values (not binary complete/total)
    // This ensures 50% checklist approval = 50% stage progress, not 0% until all items are 100%
    for (const stage of stageMap.values()) {
      stage.scheduleProgress.progressPercentage =
        stage.scheduleProgress.totalItems > 0
          ? Math.round(stage.scheduleProgress.totalProgress / stage.scheduleProgress.totalItems)
          : 0;

      for (const sub of stage.subStageMap.values()) {
        sub.scheduleProgress.progressPercentage =
          sub.scheduleProgress.totalItems > 0
            ? Math.round(sub.scheduleProgress.totalProgress / sub.scheduleProgress.totalItems)
            : 0;
      }
    }

    return STAGE_ORDER.map((name) => {
      const s = stageMap.get(name)!;
      const cfg = STAGE_CONFIG[name];
      const checklistCompletionPct = s.checklistCount > 0
        ? Math.round((s.completedChecklistCount / s.checklistCount) * 100)
        : 0;

      // Compute per-stage checklist final approval % using actual checklist items
      const stageChecklistProg = computeStageChecklistProgress(s.stageChecklistItems);
      const checklistFinalPct = stageChecklistProg.finalPct;

      // Binary schedule progress: completedItems / totalItems — uses UNIFIED completion
      const scheduleBinaryPct = s.scheduleProgress.totalItems > 0
        ? Math.round((s.scheduleProgress.completedItems / s.scheduleProgress.totalItems) * 100)
        : 0;

      // Combined stage progress:
      // - If stage has checklist items: 50% schedule + 50% checklist
      // - If stage has NO checklist items: 100% schedule (checklist doesn't apply)
      const combinedProgress = s.checklistCount > 0
        ? Math.round(scheduleBinaryPct * 0.5 + checklistFinalPct * 0.5)
        : scheduleBinaryPct;

      const stageStatus = computeStageStatus(s.scheduleProgress, s.scheduleProgressFinal, s.scheduleItems, checklistFinalPct, s.checklistCount);

      // Debug logging
      if (import.meta.env.DEV && s.scheduleProgress.totalItems > 0) {
        // eslint-disable-next-line no-console
        console.log('Stage Progress Summary', {
          stageId: name,
          stageName: name,
          scheduleProgress: scheduleBinaryPct,
          checklistProgress: checklistFinalPct,
          combinedProgress: combinedProgress,
          status: stageStatus,
          scheduleItemsCount: s.scheduleItems.length,
          scheduleCompletedItems: s.scheduleProgress.completedItems,
          scheduleTotalItems: s.scheduleProgress.totalItems,
          scheduleFinalCompletedItems: s.scheduleProgressFinal.completedItems,
          scheduleFinalTotalItems: s.scheduleProgressFinal.totalItems,
          stageChecklistItems: s.stageChecklistItems.length,
          stageChecklistFinalCompleted: stageChecklistProg.finalCompleted,
          stageChecklistFinalTotal: stageChecklistProg.finalTotal,
        });
      }

      // Build construction sub-stages
      let constructionSubStages: ConstructionSubStageData[] | null = null;
      if (name === 'Construction') {
        constructionSubStages = CONSTRUCTION_SUB_STAGE_ORDER
          .filter((subName) => {
            const sub = s.subStageMap.get(subName);
            return sub && sub.scheduleProgress.totalItems > 0;
          })
          .map((subName) => {
            const sub = s.subStageMap.get(subName)!;
            const subCfg = CONSTRUCTION_SUB_STAGE_CONFIG[subName];
            const subChecklistPct = sub.checklistCount > 0
              ? Math.round((sub.completedChecklistCount / sub.checklistCount) * 100)
              : 0;

            // Per-sub-stage checklist final approval %
            const subChecklistProg = computeStageChecklistProgress(sub.stageChecklistItems);
            const subChecklistFinalPct = subChecklistProg.finalPct;
            const subScheduleBinaryPct = sub.scheduleProgress.totalItems > 0
              ? Math.round((sub.scheduleProgress.completedItems / sub.scheduleProgress.totalItems) * 100)
              : 0;
            const subCombinedProgress = sub.checklistCount > 0
              ? Math.round(subScheduleBinaryPct * 0.5 + subChecklistFinalPct * 0.5)
              : subScheduleBinaryPct;
            const subStatus = computeStageStatus(sub.scheduleProgress, sub.scheduleProgressFinal, sub.scheduleItems, subChecklistFinalPct, sub.checklistCount);

            return {
              name: subName,
              paymentPercent: subCfg.paymentPercent,
              icon: subCfg.icon,
              accentColor: subCfg.accentColor,
              bgColor: subCfg.bgColor,
              scheduleProgress: sub.scheduleProgress,
              checklistCount: sub.checklistCount,
              completedChecklistCount: sub.completedChecklistCount,
              checklistCompletionPct: subChecklistPct,
              plannedStartDate: sub.plannedStartDate,
              plannedEndDate: sub.plannedEndDate,
              status: subStatus,
              scheduleItems: sub.scheduleItems,
              combinedProgress: subCombinedProgress,
              scheduleBinaryPct: subScheduleBinaryPct,
              checklistFinalPct: subChecklistFinalPct,
            };
          });
      }

      return {
        name,
        icon: cfg.icon,
        accentColor: cfg.accent,
        bgColor: cfg.bg,
        scheduleProgress: s.scheduleProgress,
        checklistCount: s.checklistCount,
        completedChecklistCount: s.completedChecklistCount,
        checklistCompletionPct,
        plannedStartDate: s.plannedStartDate,
        plannedEndDate: s.plannedEndDate,
        status: stageStatus,
        scheduleItems: s.scheduleItems,
        constructionSubStages,
        combinedProgress,
        scheduleBinaryPct,
        checklistFinalPct,
        scheduleFinalProgress: s.scheduleProgressFinal.totalItems > 0
          ? Math.round((s.scheduleProgressFinal.completedItems / s.scheduleProgressFinal.totalItems) * 100)
          : 0,
        scheduleProgressFinal: s.scheduleProgressFinal,
      };
    }).concat(
      (() => {
        const unassigned = stageMap.get(UNASSIGNED_STAGE);
        if (!unassigned || unassigned.scheduleProgress.totalItems === 0) return [];
        const cfg = STAGE_CONFIG[UNASSIGNED_STAGE];
        const checklistCompletionPct = unassigned.checklistCount > 0
          ? Math.round((unassigned.completedChecklistCount / unassigned.checklistCount) * 100)
          : 0;
        const stageChecklistProg = computeStageChecklistProgress(unassigned.stageChecklistItems);
        const checklistFinalPct = stageChecklistProg.finalPct;
        const scheduleBinaryPct = unassigned.scheduleProgress.totalItems > 0
          ? Math.round((unassigned.scheduleProgress.completedItems / unassigned.scheduleProgress.totalItems) * 100)
          : 0;
        const combinedProgress = unassigned.checklistCount > 0
          ? Math.round(scheduleBinaryPct * 0.5 + checklistFinalPct * 0.5)
          : scheduleBinaryPct;
        const stageStatus = computeStageStatus(
          unassigned.scheduleProgress,
          unassigned.scheduleProgressFinal,
          unassigned.scheduleItems,
          checklistFinalPct,
          unassigned.checklistCount
        );
        return [{
          name: UNASSIGNED_STAGE,
          icon: cfg.icon,
          accentColor: cfg.accent,
          bgColor: cfg.bg,
          scheduleProgress: unassigned.scheduleProgress,
          checklistCount: unassigned.checklistCount,
          completedChecklistCount: unassigned.completedChecklistCount,
          checklistCompletionPct,
          plannedStartDate: unassigned.plannedStartDate,
          plannedEndDate: unassigned.plannedEndDate,
          status: stageStatus,
          scheduleItems: unassigned.scheduleItems,
          constructionSubStages: null,
          combinedProgress,
          scheduleBinaryPct,
          checklistFinalPct,
          scheduleFinalProgress: unassigned.scheduleProgressFinal.totalItems > 0
            ? Math.round((unassigned.scheduleProgressFinal.completedItems / unassigned.scheduleProgressFinal.totalItems) * 100)
            : 0,
          scheduleProgressFinal: unassigned.scheduleProgressFinal,
        }];
      })()
    );
  }, [scheduleItems, checklistItems, linkedMap, mappings]);

  // Suggested current stage: first stage that is In Progress or Delayed,
  // then first Not Started, then last stage if all completed.
  // This ensures the active stage is the one with actual work happening.
  const suggestedCurrentStage = useMemo((): string => {
    const canonicalStages = stages.filter((s) => s.name !== UNASSIGNED_STAGE);
    // First: find any In Progress or Delayed stage
    const activeStage = canonicalStages.find((s) => s.status === 'In Progress' || s.status === 'Delayed');
    if (activeStage) return activeStage.name;
    // Second: find first Not Started stage (next to work on)
    const nextStage = canonicalStages.find((s) => s.status === 'Not Started' && s.scheduleProgress.totalItems > 0);
    if (nextStage) return nextStage.name;
    // Third: if all completed, return Handover
    const allCompleted = canonicalStages.every((s) => s.status === 'Completed' || s.scheduleProgress.totalItems === 0);
    if (allCompleted) return 'Handover';
    // Fallback: first canonical stage with items
    const firstWithItems = canonicalStages.find((s) => s.scheduleProgress.totalItems > 0);
    return firstWithItems?.name || 'Planning';
  }, [stages]);

  // Check if any stage just completed (for prompts)
  const getStageCompletionPrompt = useCallback(
    (updatedItem: ScheduleItem): string | null => {
      const itemStage = getMainStage(updatedItem);
      if (!itemStage || itemStage === UNASSIGNED_STAGE) return null;

      const stageItems = scheduleItems.filter(
        (s) => getMainStage(s) === itemStage && s.is_included !== false
      );
      if (stageItems.length === 0) return null;

      const allComplete = stageItems.every((s) => s.is_complete || s.progress >= 100);
      if (!allComplete) return null;

      const stageIndex = STAGE_ORDER.indexOf(itemStage);
      if (stageIndex === -1 || stageIndex === STAGE_ORDER.length - 1) return null;

      const nextStageName = STAGE_ORDER[stageIndex + 1];
      if (projectStage === nextStageName) return null;

      return `${itemStage} is now complete. Move project to ${nextStageName}?`;
    },
    [scheduleItems, projectStage]
  );

  // Update project stage (only when management confirms)
  const updateProjectStage = useCallback(async (newStage: string): Promise<boolean> => {
    if (!projectId) return false;
    const result = await updateProjectCurrentStage(projectId, newStage);
    if (!result.ok) {
      console.error('[useStageScheduleChecklist] updateProjectStage failed:', result.error);
      return false;
    }
    setProjectStage(newStage);
    return true;
  }, [projectId]);

  const moveUnassignedToConstruction = useCallback(async (): Promise<{
    success: boolean;
    count: number;
    error?: string;
  }> => {
    if (!projectId || !isManualScheduleProjectType(projectType)) {
      return { success: false, count: 0, error: 'Not a manual schedule project' };
    }

    const toMove = scheduleItems.filter(
      (si) => getMainStage(si) === UNASSIGNED_STAGE && !si.source_template_item_id
    );
    if (toMove.length === 0) {
      return { success: true, count: 0 };
    }

    setMovingUnassigned(true);
    const now = new Date().toISOString();
    const ids = toMove.map((si) => si.id);

    try {
      const batchSize = 50;
      for (let i = 0; i < ids.length; i += batchSize) {
        const batch = ids.slice(i, i + batchSize);
        const { error } = await supabase
          .from('jphph_project_schedule_items')
          .update({ main_stage: 'Construction', updated_at: now })
          .in('id', batch)
          .eq('project_id', projectId)
          .eq('is_deleted', false);
        if (error) {
          return { success: false, count: 0, error: error.message };
        }
      }

      setScheduleItems((prev) =>
        prev.map((si) =>
          ids.includes(si.id) ? { ...si, main_stage: 'Construction', updated_at: now } : si
        )
      );

      try {
        await persistProjectSummary(projectId);
      } catch (summaryErr) {
        console.error('[moveUnassignedToConstruction] persistProjectSummary failed:', summaryErr);
      }

      try {
        window.dispatchEvent(
          new CustomEvent('__jphph_schedule_sync__', {
            detail: {
              projectId,
              bulkItemIds: ids,
              isComplete: false,
              timestamp: now,
              action: 'move_unassigned_to_construction',
            },
          })
        );
      } catch { /* noop */ }

      return { success: true, count: ids.length };
    } finally {
      setMovingUnassigned(false);
    }
  }, [projectId, projectType, scheduleItems]);

  // Stage summary for progress tab
  const stageSummary = useMemo(() => {
    return stages.map((stage) => ({
      name: stage.name,
      status: stage.status,
      scheduleProgress: stage.scheduleProgress,
      checklistCompletionPct: stage.checklistCompletionPct,
      checklistCount: stage.checklistCount,
      completedChecklistCount: stage.completedChecklistCount,
      accentColor: stage.accentColor,
      bgColor: stage.bgColor,
      icon: stage.icon,
      combinedProgress: stage.combinedProgress,
      scheduleBinaryPct: stage.scheduleBinaryPct,
      checklistFinalPct: stage.checklistFinalPct,
    }));
  }, [stages]);

  // Overall schedule progress = FINAL progress (unmapped items count as complete).
  // This ensures that items with no checklist mapping don't permanently block 100%.
  // The DISPLAY progress (scheduleProgress) is used for per-stage UI bars.
  // The FINAL progress (scheduleProgressFinal) is used for project-level calculations.
  const overallScheduleProgress = useMemo(() => {
    let total = 0;
    let completed = 0;
    for (const stage of stages) {
      if (stage.name === UNASSIGNED_STAGE) continue;
      total += stage.scheduleProgressFinal.totalItems;
      completed += stage.scheduleProgressFinal.completedItems;
    }
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }, [stages]);

  // Combined project progress using live schedule + checklist
  const checklistProgress = useMemo((): ChecklistProgress => {
    const prog = computeChecklistProgress(checklistItems);
    // Debug logging
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('Checklist summary', {
        managementCompleted: prog.mgmtChecked,
        managementTotal: prog.mgmtRequired,
        subcontractorCompleted: prog.subChecked,
        subcontractorTotal: prog.subRequired,
        finalCompleted: prog.finalCompleted,
        finalTotal: prog.finalTotal,
        overallPct: prog.overallPct,
      });
    }
    return prog;
  }, [checklistItems]);

  const combinedProjectProgress = useMemo((): number => {
    const schedulePct = overallScheduleProgress;
    const checklistPct = checklistProgress.finalTotal > 0
      ? Math.round((checklistProgress.finalCompleted / checklistProgress.finalTotal) * 100)
      : 0;
    const combined = Math.round(schedulePct * 0.5 + checklistPct * 0.5);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('Project progress calculation (live)', {
        scheduleProgress: schedulePct,
        scheduleCompleted: stages.reduce((s, st) => s + st.scheduleProgress.completedItems, 0),
        scheduleTotal: stages.reduce((s, st) => s + st.scheduleProgress.totalItems, 0),
        checklistFinalProgress: checklistPct,
        checklistFinalCompleted: checklistProgress.finalCompleted,
        checklistFinalTotal: checklistProgress.finalTotal,
        projectProgress: combined,
      });
    }
    return combined;
  }, [overallScheduleProgress, checklistProgress, stages]);

  const scheduleItemsByStage = useMemo(() => {
    const map = new Map<string, ScheduleItem[]>();
    for (const name of STAGE_ORDER) map.set(name, []);
    for (const item of scheduleItems) {
      const stageName = getMainStage(item);
      const arr = map.get(stageName) || [];
      arr.push(item);
      map.set(stageName, arr);
    }
    return map;
  }, [scheduleItems]);

  // All schedule items enriched with stage info
  const allScheduleItemsWithStage = useMemo(() => {
    return scheduleItems.map((item) => ({
      ...item,
      stage: getMainStage(item),
      subStage: getConstructionSubStage(item),
    }));
  }, [scheduleItems]);

  const getLinkedForScheduleItem = useCallback(
    (scheduleItemId: string): LinkedChecklistItem[] => linkedMap.get(scheduleItemId) || [],
    [linkedMap]
  );

  const getMappingsForChecklistItem = useCallback(
    (checklistItemId: string): { scheduleItemId: string; mappingId: string }[] =>
      checklistToSchedulesMap.get(checklistItemId) || [],
    [checklistToSchedulesMap]
  );

  const mapChecklistToSchedule = useCallback(async (
    checklistItemId: string,
    scheduleItemId: string
  ): Promise<boolean> => {
    if (!defaultTemplateItemId || !projectId) return false;
    setMapping(true);
    try {
      const { data, error: err } = await supabase
        .from('jphph_checklist_schedule_item_mappings')
        .insert({
          checklist_template_item_id: defaultTemplateItemId,
          project_checklist_item_id: checklistItemId,
          project_schedule_item_id: scheduleItemId,
          mapping_type: 'manual',
          confidence: 'manual',
          reason: 'Manually mapped by management user',
        })
        .select()
        .maybeSingle();

      if (err || !data) return false;

      setMappings((prev) => [...prev, {
        id: (data as MappingRow).id,
        checklist_template_item_id: defaultTemplateItemId,
        project_checklist_item_id: checklistItemId,
        project_schedule_item_id: scheduleItemId,
        mapping_type: 'manual',
        confidence: 'manual',
        reason: 'Manually mapped by management user',
        schedule_template_item_id: null,
      }]);
      return true;
    } catch {
      return false;
    } finally {
      setMapping(false);
    }
  }, [defaultTemplateItemId, projectId]);

  const unmapChecklistFromSchedule = useCallback(async (
    mappingId: string
  ): Promise<boolean> => {
    setMapping(true);
    try {
      const { error: err } = await supabase
        .from('jphph_checklist_schedule_item_mappings')
        .delete()
        .eq('id', mappingId);

      if (err) return false;

      setMappings((prev) => prev.filter((m) => m.id !== mappingId));
      return true;
    } catch {
      return false;
    } finally {
      setMapping(false);
    }
  }, []);

  const linkChecklistItem = useCallback((checklistItemId: string, scheduleItemId: string) => {
    if (!defaultTemplateItemId) return;
    const newId = crypto.randomUUID();
    setMappings((prev) => [...prev, {
      id: newId,
      checklist_template_item_id: defaultTemplateItemId,
      project_checklist_item_id: checklistItemId,
      project_schedule_item_id: scheduleItemId,
      mapping_type: 'manual',
      confidence: 'manual',
      reason: 'Manually linked from schedule item modal',
      schedule_template_item_id: null,
    }]);
  }, [defaultTemplateItemId]);

  const unlinkChecklistItem = useCallback((mappingId: string) => {
    setMappings((prev) => prev.filter((m) => m.id !== mappingId));
  }, []);

  /**
   * Batch sync: recalculate and persist progress for ALL schedule items
   * based on their current linked checklist state.
   * Called once on load to fix stale schedule progress values.
   */
  const batchSyncScheduleProgress = useCallback(async (): Promise<void> => {
    if (scheduleItems.length === 0) return;
    const now = new Date().toISOString();
    const updates: Array<{ id: string; progress: number; is_complete: boolean }> = [];

    for (const si of scheduleItems) {
      if (si.is_included === false) continue;
      const linked = linkedMap.get(si.id) || [];
      if (linked.length === 0) continue; // no checklist linked — skip

      const { progressPct, allRequiredComplete } = computeLinkedProgress(linked);

      // CRITICAL FIX: Do NOT overwrite manual completion.
      // If the DB already has is_complete=true, keep it true regardless of checklist state.
      // If the DB already has progress >= 100, keep it complete regardless of checklist state.
      // The batch sync is only for fixing stale progress values from checklist changes.
      // Manual completion is a management decision, not something to be auto-undone.
      const nextIsComplete = si.is_complete || si.progress >= 100 || allRequiredComplete;

      // Only update if DB value differs from calculated value
      if (si.progress !== progressPct || si.is_complete !== nextIsComplete) {
        updates.push({ id: si.id, progress: progressPct, is_complete: nextIsComplete });
      }
    }

    if (updates.length === 0) return;

    // Batch update in chunks of 50
    const CHUNK = 50;
    for (let i = 0; i < updates.length; i += CHUNK) {
      const chunk = updates.slice(i, i + CHUNK);
      // Update each item individually (Supabase doesn't support batch upsert with different values)
      await Promise.all(
        chunk.map((u) =>
          supabase
            .from('jphph_project_schedule_items')
            .update({ progress: u.progress, is_complete: u.is_complete, updated_at: now })
            .eq('id', u.id)
        )
      );
    }

    // Update local state
    setScheduleItems((prev) =>
      prev.map((si) => {
        const u = updates.find((x) => x.id === si.id);
        if (!u) return si;
        return { ...si, progress: u.progress, is_complete: u.is_complete, updated_at: now };
      })
    );
  }, [scheduleItems, linkedMap, projectId]);

  // After initial data load, run a one-time batch sync to fix stale schedule progress values.
  // This ensures schedule items reflect current checklist state without requiring manual toggles.
  // NOTE: this useEffect is placed AFTER batchSyncScheduleProgress is defined to avoid
  // temporal dead zone error (Cannot access 'batchSyncScheduleProgress' before initialization).
  useEffect(() => {
    if (!enabled) return;
    if (loading) return;
    if (hasSynced) return;
    if (scheduleItems.length === 0 || checklistItems.length === 0) return;
    // Only run if there are linked mappings
    if (mappings.length === 0) return;
    setHasSynced(true);
    // Defer batch sync so checklist UI can paint first.
    const runSync = () => {
      batchSyncScheduleProgress().catch(() => { /* noop */ });
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runSync, { timeout: 4000 });
    } else {
      setTimeout(runSync, 250);
    }
  }, [loading, scheduleItems, checklistItems, mappings, hasSynced, batchSyncScheduleProgress, enabled]);

  /**
   * After a checklist item toggle, recalculate progress for all linked schedule
   * items and update them in Supabase + local state. This keeps Schedule % display
   * in sync without reloading all 156 items.
   */
  const updateScheduleProgressFromChecklist = useCallback(async (
    checklistItemId: string
  ): Promise<void> => {
    const linkedSchedules = getMappingsForChecklistItem(checklistItemId);
    if (linkedSchedules.length === 0) return;

    const now = new Date().toISOString();

    for (const { scheduleItemId } of linkedSchedules) {
      const linked = getLinkedForScheduleItem(scheduleItemId);
      const { progressPct, allRequiredComplete } = computeLinkedProgress(linked);

      // CRITICAL FIX: Do NOT overwrite manual completion.
      // Get the current schedule item to check its is_complete flag and progress.
      const currentItem = scheduleItems.find((s) => s.id === scheduleItemId);
      const isManuallyComplete = (currentItem?.is_complete ?? false) || (currentItem?.progress ?? 0) >= 100;

      // If manually complete or progress=100, keep is_complete=true regardless of checklist state.
      // Only update progress from checklist; never unmark manual completion.
      const nextIsComplete = isManuallyComplete || allRequiredComplete;

      // Update Supabase — only progress and completion, never dates or links
      await supabase
        .from('jphph_project_schedule_items')
        .update({
          progress: progressPct,
          is_complete: nextIsComplete,
          updated_at: now,
        })
        .eq('id', scheduleItemId);

      // Optimistic local update for scheduleItems in this hook
      setScheduleItems((prev) =>
        prev.map((si) =>
          si.id === scheduleItemId
            ? { ...si, progress: progressPct, is_complete: nextIsComplete, updated_at: now }
            : si
        )
      );
    }
  }, [getMappingsForChecklistItem, getLinkedForScheduleItem, scheduleItems]);

  /* ── Optimistic inline toggle for a checklist item (role-aware) ── */
  const toggleChecklistItem = useCallback(async (item: ChecklistItemMinimal, userId: string, role: 'management' | 'subcontractor' | 'client' = 'management') => {
    const field = role === 'subcontractor' ? 'subcontractor_checked' : 'management_checked';
    const dateField = role === 'subcontractor' ? 'subcontractor_checked_at' : 'management_checked_at';
    const byField = role === 'subcontractor' ? 'subcontractor_checked_by' : 'management_checked_by';

    const nextValue = !(item as Record<string, boolean>)[field];
    const now = new Date().toISOString();

    // Compute what is_completed will be after this toggle (respecting requirements)
    const requiresMgmt = item.requires_management_check !== false;
    const requiresSub = item.requires_subcontractor_check === true;
    const nextMgmt = field === 'management_checked' ? nextValue : item.management_checked;
    const nextSub = field === 'subcontractor_checked' ? nextValue : item.subcontractor_checked;
    const nextCompleted = (!requiresMgmt || nextMgmt) && (!requiresSub || nextSub);

    /* Optimistic local update */
    setTogglingId(item.id);
    setChecklistItems((prev) =>
      prev.map((c) =>
        c.id === item.id
          ? {
              ...c,
              [field]: nextValue,
              [dateField]: nextValue ? now : null,
              [byField]: nextValue ? userId : null,
              is_completed: nextCompleted,
              completed_at: nextCompleted ? now : null,
              completed_by: nextCompleted ? userId : null,
              updated_at: now,
            }
          : c
      )
    );

    const update: Record<string, unknown> = {
      [field]: nextValue,
      [dateField]: nextValue ? now : null,
      [byField]: nextValue ? userId : null,
      is_completed: nextCompleted,
      completed_at: nextCompleted ? now : null,
      completed_by: nextCompleted ? userId : null,
      updated_at: now,
    };

    const { error: err } = await supabase
      .from('jphph_checklist_items')
      .update(update)
      .eq('id', item.id);

    if (err) {
      // eslint-disable-next-line no-console
      console.error('toggleChecklistItem error:', err.message);
      /* Revert on failure */
      setChecklistItems((prev) =>
        prev.map((c) => (c.id === item.id ? item : c))
      );
    } else {
      // After successful DB update, recalculate and push progress to linked schedule items
      await updateScheduleProgressFromChecklist(item.id);
      // Also invalidate the schedule cache so ScheduleTab shows updated progress
      try {
        const { scheduleCache } = await import('./useProjectSchedule');
        scheduleCache.delete(projectId);
      } catch { /* noop */ }
      // Broadcast sync to other hook instances on the same page
      broadcastSync({ projectId, itemId: item.id, field, newValue: nextValue, isCompleted: nextCompleted });
      // Also broadcast to other browser tabs via storage
      try {
        window.localStorage.setItem(SYNC_KEY, JSON.stringify({ projectId, itemId: item.id, field, newValue: nextValue, isCompleted: nextCompleted }));
        window.localStorage.removeItem(SYNC_KEY);
      } catch { /* noop */ }
      // CRITICAL: Also dispatch __jphph_schedule_sync__ so useProjectSummary
      // (header, dashboard, daily log) all get notified after a checklist change.
      try {
        window.dispatchEvent(
          new CustomEvent('__jphph_schedule_sync__', {
            detail: {
              projectId,
              isComplete: nextCompleted,
              timestamp: new Date().toISOString(),
              source: 'toggleChecklistItem',
              checklistItemId: item.id,
            },
          })
        );
      } catch { /* noop */ }
    }

    setTogglingId(null);
    return !err;
  }, [updateScheduleProgressFromChecklist, projectId]);

  // Debug output — Schedule completion debug with unified logic
  useEffect(() => {
    if (!JPH_DEBUG) return;
    if (loading) return;
    if (scheduleItems.length === 0) return;

    // Build schedule summary counts using the SAME shared completion logic
    let totalScheduleItems = 0;
    let completedScheduleItems = 0;
    let pendingScheduleItems = 0;

    for (const item of scheduleItems) {
      if (item.is_included === false) continue;
      totalScheduleItems += 1;
      const linked = linkedMap.get(item.id) || [];
      const isComplete = isScheduleItemFinalComplete(
        { is_complete: item.is_complete, progress: item.progress, actual_end_date: item.actual_end_date },
        linked
      );
      if (isComplete) completedScheduleItems += 1;
      else pendingScheduleItems += 1;
    }

    const scheduleFinalProgress = totalScheduleItems > 0
      ? Math.round((completedScheduleItems / totalScheduleItems) * 100)
      : 0;

    // Comprehensive per-item debug log (matches user requirement #10)
    const debugItems = scheduleItems.map((si) => {
      const linked = linkedMap.get(si.id) || [];
      const { allRequiredComplete, progressPct } = computeLinkedProgress(linked);
      const isComplete = isScheduleItemFinalComplete(
        { is_complete: si.is_complete, progress: si.progress, actual_end_date: si.actual_end_date },
        linked
      );

      let reasonPending = '';
      if (!isComplete) {
        if (linked.length === 0) reasonPending = 'Not completed (no checklist mapped)';
        else if (!allRequiredComplete) {
          const missingMgmt = linked.filter((i) => (i.requires_management_check !== false) && !i.management_checked).length;
          const missingSub = linked.filter((i) => (i.requires_subcontractor_check === true) && !i.subcontractor_checked).length;
          reasonPending = `${missingMgmt} mgmt + ${missingSub} sub pending`;
        } else {
          reasonPending = 'Data error — checklist complete but not counted';
        }
      }

      return {
        id: si.id,
        title: si.title,
        status: si.status,
        progress: si.progress,
        final_progress: si.progress,
        is_completed: si.is_complete,
        manual_complete: si.is_complete,
        completed_at: si.actual_end_date || null,
        actual_end_date: si.actual_end_date || null,
        hasChecklistMapping: linked.length > 0,
        displayedAsComplete: si.is_complete || si.progress >= 100,
        countedAsComplete: isComplete,
        reasonPending: reasonPending || null,
        linkedChecklistCount: linked.length,
        checklistComplete: allRequiredComplete,
        checklistProgress: progressPct,
      };
    });

    jphDebugLog('Schedule completion debug', debugItems);

    jphDebugLog('Schedule summary debug', {
      scheduleTotal: totalScheduleItems,
      scheduleCompleted: completedScheduleItems,
      schedulePending: pendingScheduleItems,
      scheduleFinalProgress,
    });

    // Also log the mapping debug for traceability
    const debugScheduleItems = scheduleItems.map((si) => {
      const linked = linkedMap.get(si.id) || [];
      const { progressPct, allRequiredComplete } = computeLinkedProgress(linked);
      const isComplete = isScheduleItemFinalComplete(
        { is_complete: si.is_complete, progress: si.progress, actual_end_date: si.actual_end_date },
        linked
      );

      let reasonNot100: string;
      if (isComplete) {
        if (si.is_complete) reasonNot100 = 'Manual complete';
        else if (si.progress >= 100) reasonNot100 = 'Progress 100%';
        else if (si.actual_end_date) reasonNot100 = 'Actual end date set';
        else if (allRequiredComplete) reasonNot100 = 'Checklist complete';
        else reasonNot100 = 'Complete';
      } else {
        if (linked.length === 0) reasonNot100 = 'No checklist mapped — not complete';
        else if (!allRequiredComplete) reasonNot100 = 'Checklist incomplete';
        else reasonNot100 = 'Data error';
      }

      return {
        id: si.id,
        title: si.title,
        stageId: getMainStage(si),
        isComplete,
        finalProgress: isComplete ? 100 : (linked.length > 0 ? progressPct : 0),
        mappedChecklistCount: linked.length,
        completedMappedChecklistCount: linked.filter((l) => l.is_completed).length,
        checklistAllApproved: allRequiredComplete,
        reasonNot100,
      };
    });

    jphDebugLog('Schedule Item Mapping Debug', debugScheduleItems);
  }, [loading, scheduleItems, linkedMap]);

  return {
    stages,
    scheduleItems,
    checklistItems,
    mappings,
    linkedMap,
    loading,
    error,
    mapping,
    togglingId,
    projectStage,
    suggestedCurrentStage,
    stageSummary,
    projectType,
    movingUnassigned,
    moveUnassignedToConstruction,
    overallScheduleProgress,
    checklistProgress,
    combinedProjectProgress,
    reload: () => {
      // Force immediate reload — no cache to invalidate since cache was removed
      setHasSynced(false); // allow batch sync to run again after reload
      load(true);
    },
    batchSyncScheduleProgress,
    getLinkedForScheduleItem,
    getMappingsForChecklistItem,
    mapChecklistToSchedule,
    unmapChecklistFromSchedule,
    linkChecklistItem,
    unlinkChecklistItem,
    toggleChecklistItem,
    unmappedChecklistItems,
    scheduleItemsByStage,
    allScheduleItemsWithStage,
    getStageCompletionPrompt,
    updateProjectStage,
    checklistToSchedulesMap,
  };
}